import React, { useState, useMemo } from "react";
import { ChevronRight, Search, ExternalLink, BookOpen, X, ChevronDown, ChevronUp } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";

interface GuidelineLink {
  source: string;
  title: string;
  code: string;
  url: string;
  year: number;
}

interface DiseaseProtocol {
  id: string;
  name: string;
  icd: string;
  category: string;
  categoryColor: string;
  guidelines: GuidelineLink[];
  keyPoints: string[];
}

const SOURCES = ["All", "NICE", "BAD", "AAD", "EDF"];

const SOURCE_STYLE: Record<string, { badge: string; link: string }> = {
  NICE: { badge: "bg-blue-100 text-blue-800 border border-blue-200", link: "hover:bg-blue-50 border-blue-100" },
  BAD:  { badge: "bg-purple-100 text-purple-800 border border-purple-200", link: "hover:bg-purple-50 border-purple-100" },
  AAD:  { badge: "bg-red-100 text-red-800 border border-red-200", link: "hover:bg-red-50 border-red-100" },
  EDF:  { badge: "bg-green-100 text-green-800 border border-green-200", link: "hover:bg-green-50 border-green-100" },
  WHO:  { badge: "bg-cyan-100 text-cyan-800 border border-cyan-200", link: "hover:bg-cyan-50 border-cyan-100" },
};

const CAT_STYLE: Record<string, string> = {
  "Inflammatory":       "bg-orange-50 text-orange-700 border border-orange-200",
  "Immune-Mediated":    "bg-violet-50 text-violet-700 border border-violet-200",
  "Malignant":          "bg-rose-50 text-rose-700 border border-rose-200",
  "Pre-malignant":      "bg-amber-50 text-amber-700 border border-amber-200",
  "Infectious":         "bg-teal-50 text-teal-700 border border-teal-200",
  "Autoimmune Bullous": "bg-indigo-50 text-indigo-700 border border-indigo-200",
};

const DISEASES: DiseaseProtocol[] = [
  {
    id: "atopic-eczema",
    name: "Atopic Eczema (Atopic Dermatitis)",
    icd: "L20",
    category: "Inflammatory",
    guidelines: [
      {
        source: "NICE",
        title: "Atopic eczema in under 12s: diagnosis and management",
        code: "CG57",
        url: "https://www.nice.org.uk/guidance/cg57",
        year: 2023,
      },
      {
        source: "NICE",
        title: "Dupilumab for treating moderate-to-severe atopic dermatitis",
        code: "TA534",
        url: "https://www.nice.org.uk/guidance/ta534",
        year: 2021,
      },
      {
        source: "BAD",
        title: "BAD Guidelines for Atopic Eczema",
        code: "2023",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Atopic Dermatitis",
        code: "2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF Guidelines — Atopic Dermatitis (ETFAD)",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Stepwise treatment: emollients → mild TCS → moderate TCS → calcineurin inhibitors → dupilumab/JAK-inhibitors",
      "Emollients should be applied liberally and frequently as the cornerstone of therapy",
      "Dupilumab (anti-IL-4/IL-13) is first-line biologic for moderate–severe disease",
      "JAK inhibitors: Upadacitinib, Abrocitinib, Baricitinib approved for moderate–severe AD",
    ],
  },
  {
    id: "psoriasis",
    name: "Psoriasis (Plaque)",
    icd: "L40.0",
    category: "Immune-Mediated",
    guidelines: [
      {
        source: "NICE",
        title: "Psoriasis: assessment and management",
        code: "CG153",
        url: "https://www.nice.org.uk/guidance/cg153",
        year: 2022,
      },
      {
        source: "BAD",
        title: "BAD Guidelines for Psoriasis",
        code: "2023",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Psoriasis with Biologics",
        code: "2019",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines on the Treatment of Psoriasis",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Mild: topical agents (TCS, vitamin D analogues, combinations)",
      "Moderate–severe (PASI >10 or DLQI >10): phototherapy, methotrexate, biologics",
      "Biologics: anti-TNF (adalimumab), anti-IL-17 (secukinumab, ixekizumab), anti-IL-23 (risankizumab, guselkumab)",
      "PASI, BSA, and DLQI scores guide treatment escalation",
    ],
  },
  {
    id: "acne",
    name: "Acne Vulgaris",
    icd: "L70.0",
    category: "Inflammatory",
    guidelines: [
      {
        source: "NICE",
        title: "Acne vulgaris: management",
        code: "NG198",
        url: "https://www.nice.org.uk/guidance/ng198",
        year: 2021,
      },
      {
        source: "BAD",
        title: "BAD Guidelines for Acne Vulgaris",
        code: "2021",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2021,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Acne Vulgaris",
        code: "2016-2024",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2024,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Acne",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2022,
      },
    ],
    keyPoints: [
      "Comedonal: topical retinoids (adapalene, tretinoin) are first-line",
      "Inflammatory: benzoyl peroxide ± topical antibiotics (limit antibiotic monotherapy)",
      "Moderate–severe: oral doxycycline + topical retinoid",
      "Severe/scarring: isotretinoin (monitor liver, lipids, iPLEDGE for females)",
    ],
  },
  {
    id: "melanoma",
    name: "Melanoma",
    icd: "C43",
    category: "Malignant",
    guidelines: [
      {
        source: "NICE",
        title: "Melanoma: assessment and management",
        code: "NG14",
        url: "https://www.nice.org.uk/guidance/ng14",
        year: 2022,
      },
      {
        source: "BAD",
        title: "BAD Melanoma Guidelines",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Melanoma",
        code: "2019-2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF–EADO–EORTC S3 Guidelines — Cutaneous Melanoma",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2022,
      },
    ],
    keyPoints: [
      "Excision margins: in situ 0.5–1 cm; <1 mm 1 cm; 1–2 mm 1–2 cm; >2 mm 2–3 cm",
      "Sentinel lymph node biopsy for tumours ≥0.8 mm",
      "Adjuvant: pembrolizumab, nivolumab, or dabrafenib+trametinib (BRAF V600+)",
      "Advanced: anti-PD-1 ± anti-CTLA-4; BRAF/MEK inhibitors for BRAF-mutant",
    ],
  },
  {
    id: "bcc",
    name: "Basal Cell Carcinoma (BCC)",
    icd: "C44.91",
    category: "Malignant",
    guidelines: [
      {
        source: "NICE",
        title: "Non-melanoma skin cancer: organising primary and specialist care",
        code: "NG12",
        url: "https://www.nice.org.uk/guidance/ng12",
        year: 2022,
      },
      {
        source: "BAD",
        title: "BAD Guidelines for Management of BCC",
        code: "2021",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2021,
      },
      {
        source: "AAD",
        title: "AAD Guidelines — Keratinocyte Carcinoma",
        code: "2018-2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
    ],
    keyPoints: [
      "Low-risk: surgical excision with 4 mm margins; curettage acceptable for superficial lesions",
      "High-risk/facial: Mohs micrographic surgery preferred",
      "Vismodegib or sonidegib (hedgehog pathway inhibitors) for advanced/metastatic BCC",
      "Radiotherapy for patients unfit for surgery",
    ],
  },
  {
    id: "scc",
    name: "Squamous Cell Carcinoma (SCC)",
    icd: "C44.92",
    category: "Malignant",
    guidelines: [
      {
        source: "NICE",
        title: "Non-melanoma skin cancer: organising primary and specialist care",
        code: "NG12",
        url: "https://www.nice.org.uk/guidance/ng12",
        year: 2022,
      },
      {
        source: "BAD",
        title: "BAD Guidelines for Management of cSCC",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "AAD",
        title: "AAD Guidelines — Keratinocyte Carcinoma",
        code: "2018-2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Cutaneous SCC",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2022,
      },
    ],
    keyPoints: [
      "Low-risk: excision with 4 mm margin",
      "High-risk (>2 cm, poorly differentiated, perineural): 6 mm margin or Mohs",
      "Cemiplimab (anti-PD-1) first-line for advanced/metastatic cSCC",
      "Sentinel node biopsy for high-risk or N0 at presentation",
    ],
  },
  {
    id: "actinic-keratosis",
    name: "Actinic Keratosis (Solar Keratosis)",
    icd: "L57.0",
    category: "Pre-malignant",
    guidelines: [
      {
        source: "NICE",
        title: "Non-melanoma skin cancer (includes AK)",
        code: "NG12",
        url: "https://www.nice.org.uk/guidance/ng12",
        year: 2022,
      },
      {
        source: "BAD",
        title: "BAD Guidelines — Actinic Keratosis",
        code: "2017",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
      {
        source: "AAD",
        title: "AAD Guidelines — Actinic Keratosis",
        code: "2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Actinic Keratosis",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2022,
      },
    ],
    keyPoints: [
      "Isolated lesions: cryotherapy (liquid nitrogen 5–10 sec)",
      "Field therapy: 5-FU 5% cream, imiquimod 5%, ingenol mebutate, diclofenac 3%",
      "PDT (photodynamic therapy): preferred for face/scalp field AK",
      "All patients: SPF50+ sunscreen + sun protection education",
    ],
  },
  {
    id: "urticaria",
    name: "Chronic Urticaria",
    icd: "L50.1",
    category: "Immune-Mediated",
    guidelines: [
      {
        source: "NICE",
        title: "Urticaria and angio-oedema: assessment and management",
        code: "CG145",
        url: "https://www.nice.org.uk/guidance/cg145",
        year: 2021,
      },
      {
        source: "BAD",
        title: "BAD Guidelines — Chronic Spontaneous Urticaria",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "AAD",
        title: "AAD Guidelines — Urticaria",
        code: "2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EAACI/GA²LEN/EDF/WAO — Urticaria S3 Guideline",
        code: "S3 2022",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2022,
      },
    ],
    keyPoints: [
      "Step 1: Non-sedating H1-antihistamines (cetirizine, loratadine)",
      "Step 2: Up-dose antihistamine up to 4× licensed dose",
      "Step 3: Add omalizumab (anti-IgE) — licensed for refractory CSU",
      "Step 4: Short-course cyclosporine (second line); avoid long-term steroids",
    ],
  },
  {
    id: "rosacea",
    name: "Rosacea",
    icd: "L71",
    category: "Inflammatory",
    guidelines: [
      {
        source: "BAD",
        title: "BAD Guidelines — Rosacea",
        code: "2021",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2021,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Rosacea",
        code: "2019",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2019,
      },
      {
        source: "EDF",
        title: "EDF S2k Guidelines — Rosacea",
        code: "S2k",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2019,
      },
    ],
    keyPoints: [
      "Erythematotelangiectatic: brimonidine gel (vasoconstrictor), laser/IPL",
      "Papulopustular: ivermectin 1% cream (first-line), metronidazole 0.75%",
      "Moderate–severe papulopustular: add doxycycline 40 mg MR (low-dose)",
      "Phymatous: CO₂ laser, electrosurgery",
    ],
  },
  {
    id: "alopecia-areata",
    name: "Alopecia Areata",
    icd: "L63",
    category: "Immune-Mediated",
    guidelines: [
      {
        source: "BAD",
        title: "BAD Guidelines — Alopecia Areata",
        code: "2023",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Alopecia Areata",
        code: "2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Alopecia Areata",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Mild (<50% scalp): intralesional triamcinolone acetonide (5–10 mg/mL) first-line",
      "Moderate–severe: systemic immunosuppressants (methotrexate, ciclosporin)",
      "JAK inhibitors: baricitinib (FDA/EMA approved), ritlecitinib for age ≥12",
      "Spontaneous remission in 80% of patchy AA within 1 year",
    ],
  },
  {
    id: "vitiligo",
    name: "Vitiligo",
    icd: "L80",
    category: "Immune-Mediated",
    guidelines: [
      {
        source: "BAD",
        title: "BAD Guidelines — Vitiligo",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Vitiligo",
        code: "2022",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2022,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Vitiligo",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Active disease: tacrolimus 0.1% ointment or potent TCS for face/neck",
      "Phototherapy: NB-UVB 3×/week is gold standard for generalised vitiligo",
      "Ruxolitinib cream 1.5% (JAK1/2 inhibitor) — FDA approved 2022 for non-segmental",
      "Surgical repigmentation (melanocyte transfer) for stable disease >2 years",
    ],
  },
  {
    id: "scabies",
    name: "Scabies",
    icd: "B86",
    category: "Infectious",
    guidelines: [
      {
        source: "NICE",
        title: "Scabies: antimicrobial prescribing",
        code: "NG225",
        url: "https://www.nice.org.uk/guidance/ng225",
        year: 2023,
      },
      {
        source: "BAD",
        title: "BAD Guidelines — Scabies",
        code: "2017",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
    ],
    keyPoints: [
      "Permethrin 5% cream (full body, 8–12 hrs) — first-line, repeat at 7 days",
      "Ivermectin oral (200 μg/kg) — for institutional outbreaks or treatment failure",
      "Treat all household contacts simultaneously regardless of symptoms",
      "Post-scabetic itch may persist 4–6 weeks — treat symptomatically, not as re-infection",
    ],
  },
  {
    id: "impetigo",
    name: "Impetigo",
    icd: "L01",
    category: "Infectious",
    guidelines: [
      {
        source: "NICE",
        title: "Impetigo: antimicrobial prescribing",
        code: "NG153",
        url: "https://www.nice.org.uk/guidance/ng153",
        year: 2023,
      },
      {
        source: "BAD",
        title: "BAD Guidelines — Impetigo",
        code: "2012",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
    ],
    keyPoints: [
      "Localised: topical hydrogen peroxide 1% cream or fusidic acid cream",
      "Extensive: oral flucloxacillin (or cefalexin if penicillin-allergic)",
      "MRSA-suspected: topical mupirocin or oral co-trimoxazole/doxycycline",
      "Exclude from school/work until lesions crusted or 48 hrs post antibiotic",
    ],
  },
  {
    id: "hidradenitis",
    name: "Hidradenitis Suppurativa",
    icd: "L73.2",
    category: "Inflammatory",
    guidelines: [
      {
        source: "NICE",
        title: "Hidradenitis suppurativa: diagnosis and management",
        code: "NG158",
        url: "https://www.nice.org.uk/guidance/ng158",
        year: 2021,
      },
      {
        source: "BAD",
        title: "BAD Guidelines — Hidradenitis Suppurativa",
        code: "2019",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2023,
      },
      {
        source: "AAD",
        title: "AAD Guidelines of Care — Hidradenitis Suppurativa",
        code: "2019",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF S3 Guidelines — Hidradenitis Suppurativa",
        code: "S3",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Hurley I: topical clindamycin 1% lotion ± resorcinol 15% cream",
      "Hurley II: doxycycline 100 mg BD × 12 weeks; adalimumab (anti-TNF) FDA/EMA approved",
      "Hurley III: biologics (adalimumab, secukinumab) ± surgical debridement",
      "Lifestyle: weight loss, smoking cessation, avoid tight clothing",
    ],
  },
  {
    id: "bullous-pemphigoid",
    name: "Bullous Pemphigoid",
    icd: "L12.0",
    category: "Autoimmune Bullous",
    guidelines: [
      {
        source: "BAD",
        title: "BAD Guidelines — Bullous Pemphigoid",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "EDF",
        title: "EDF S2k Guidelines — Bullous Pemphigoid",
        code: "S2k",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2023,
      },
    ],
    keyPoints: [
      "Localised: superpotent TCS (clobetasol propionate 0.05%) — preferred in elderly",
      "Generalised: systemic prednisolone (0.5 mg/kg/day) with steroid-sparing agent",
      "Steroid-sparing: doxycycline 200 mg/day, dapsone, methotrexate, azathioprine",
      "Rituximab for treatment-refractory cases",
    ],
  },
  {
    id: "contact-dermatitis",
    name: "Allergic Contact Dermatitis",
    icd: "L23",
    category: "Inflammatory",
    guidelines: [
      {
        source: "BAD",
        title: "BAD Guidelines — Contact Dermatitis",
        code: "2022",
        url: "https://www.skinhealthinfo.org.uk/for-professionals/clinical-guidelines/",
        year: 2022,
      },
      {
        source: "AAD",
        title: "AAD Guidelines — Contact Dermatitis",
        code: "2023",
        url: "https://www.aad.org/member/clinical-quality/guidelines",
        year: 2023,
      },
      {
        source: "EDF",
        title: "EDF Guidelines — Contact Dermatitis",
        code: "2020",
        url: "https://www.edf.one/home/Guidelines/Guidelines.html",
        year: 2020,
      },
    ],
    keyPoints: [
      "Patch testing (ICDRG standards) mandatory to identify allergen",
      "Standard European Baseline Series: 30 allergens tested",
      "Avoidance of identified allergen is the definitive treatment",
      "TCS for acute flares; tacrolimus for maintenance on face",
    ],
  },
];

export default function ClinicalGuidelinesViewer({ onBack }: { onBack: () => void }) {
  const [query, setQuery] = useState("");
  const [activeSource, setActiveSource] = useState("All");
  const [expanded, setExpanded] = useState<string | null>(null);

  const filtered = useMemo(() => {
    return DISEASES.filter(d => {
      const q = query.toLowerCase();
      const matchQuery =
        !q ||
        d.name.toLowerCase().includes(q) ||
        d.icd.toLowerCase().includes(q) ||
        d.category.toLowerCase().includes(q);

      const matchSource =
        activeSource === "All" ||
        d.guidelines.some(g => g.source === activeSource);

      return matchQuery && matchSource;
    });
  }, [query, activeSource]);

  const toggle = (id: string) => setExpanded(prev => (prev === id ? null : id));

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 pt-6 md:pt-12 pb-8 px-4">
        <div className="max-w-5xl mx-auto">
          <button
            onClick={onBack}
            className="mb-6 flex items-center gap-2 text-slate-300 hover:text-white transition-colors text-sm font-medium"
          >
            <ChevronRight className="w-4 h-4 rotate-180" />
            Back
          </button>

          <div className="flex items-center gap-4 mb-6">
            <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center shadow-xl">
              <BookOpen className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl lg:text-3xl font-extrabold text-white">
                Dermatology Clinical Guidelines
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                {DISEASES.length} conditions · NICE · BAD · AAD · EDF · Verified links
              </p>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search condition, ICD code, or category..."
              className="w-full bg-white/10 border border-white/20 text-white placeholder-slate-400 rounded-xl py-3 pl-11 pr-10 focus:outline-none focus:border-blue-400 focus:bg-white/15 transition-all text-sm"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2"
              >
                <X className="w-4 h-4 text-slate-400 hover:text-white" />
              </button>
            )}
          </div>

          {/* Source filters */}
          <div className="flex gap-2 flex-wrap">
            {SOURCES.map(src => (
              <button
                key={src}
                onClick={() => setActiveSource(src)}
                className={`px-4 py-1.5 rounded-full text-xs font-bold transition-all border ${
                  activeSource === src
                    ? "bg-white text-slate-900 border-white"
                    : "bg-white/10 text-slate-300 border-white/20 hover:bg-white/20"
                }`}
              >
                {src}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 max-w-5xl mx-auto w-full px-4 py-8">
        {filtered.length === 0 ? (
          <div className="text-center py-20 text-slate-400">
            <Search className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-medium">No results for "{query}"</p>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-slate-500 text-xs mb-4 font-medium">
              {filtered.length} condition{filtered.length !== 1 ? "s" : ""} — click to expand guidelines
            </p>

            {filtered.map((disease, idx) => (
              <motion.div
                key={disease.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.025 }}
                className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden"
              >
                {/* Row */}
                <button
                  className="w-full text-left p-4 md:p-5 flex items-center gap-4 hover:bg-slate-50 transition-colors"
                  onClick={() => toggle(disease.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ${CAT_STYLE[disease.category] || "bg-slate-100 text-slate-600"}`}>
                        {disease.category}
                      </span>
                      <span className="text-[10px] text-slate-400 font-mono bg-slate-100 px-2 py-0.5 rounded">
                        ICD {disease.icd}
                      </span>
                    </div>
                    <h2 className="font-bold text-slate-800 text-sm md:text-base leading-tight">
                      {disease.name}
                    </h2>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <div className="hidden sm:flex gap-1">
                      {disease.guidelines.map(g => (
                        <span
                          key={g.url}
                          className={`text-[9px] font-black px-1.5 py-0.5 rounded ${SOURCE_STYLE[g.source]?.badge}`}
                        >
                          {g.source}
                        </span>
                      ))}
                    </div>
                    {expanded === disease.id
                      ? <ChevronUp className="w-4 h-4 text-slate-400" />
                      : <ChevronDown className="w-4 h-4 text-slate-400" />
                    }
                  </div>
                </button>

                {/* Expanded */}
                <AnimatePresence>
                  {expanded === disease.id && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-slate-100 p-4 md:p-5 space-y-5">

                        {/* Key clinical points */}
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                            Key Clinical Points
                          </p>
                          <ul className="space-y-1.5">
                            {disease.keyPoints.map((pt, i) => (
                              <li key={i} className="flex gap-2 text-sm text-slate-700 leading-snug">
                                <span className="text-blue-500 font-bold shrink-0 mt-0.5">›</span>
                                {pt}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Guideline links */}
                        <div>
                          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2">
                            Official Guidelines
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {disease.guidelines.map(g => (
                              <a
                                key={g.url}
                                href={g.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`flex items-start gap-3 p-3 rounded-xl border bg-white transition-all group ${SOURCE_STYLE[g.source]?.link || "hover:bg-slate-50 border-slate-100"}`}
                              >
                                <span className={`text-[10px] font-black px-2 py-1 rounded-lg shrink-0 ${SOURCE_STYLE[g.source]?.badge}`}>
                                  {g.source} {g.code}
                                </span>
                                <span className="text-xs text-slate-700 flex-1 leading-snug font-medium">
                                  {g.title}
                                </span>
                                <div className="flex items-center gap-1 shrink-0 self-start pt-0.5">
                                  <span className="text-[10px] text-slate-400">{g.year}</span>
                                  <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-slate-600" />
                                </div>
                              </a>
                            ))}
                          </div>
                        </div>

                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-5 text-slate-400 text-xs border-t border-slate-200 px-4">
        Sources: NICE · BAD · AAD · EDF — For reference only. Always verify against the latest published version.
      </div>
    </div>
  );
}
